import React from 'react';
import { Link } from 'react-router-dom';
import { Plane, ShieldCheck, HeartHandshake, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';

/**
 * TREATX-SOFT-PRODUCTION-FINALIZATION-01 — Part B.
 *
 * Informational overview only. Not a legal document.
 * The sole authoritative legal framework for Health Tourism is the
 * TreatX Patient Terms of Service (content_key `terms_of_service`).
 * No legal clauses live in this page.
 */
const HealthTourismInfoPage = () => (
  <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8" data-testid="health-tourism-info-page">
    <SEO
      title="Health Tourism at TreatX"
      description="A brief overview of how cross-border medical treatment works on the TreatX platform — provider verification, quotes, bookings, and support."
      canonicalPath="/health-tourism-info"
    />
    <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-sm p-6 sm:p-8 lg:p-10">
      <header className="flex items-start gap-3">
        <Plane className="h-6 w-6 text-primary mt-1" aria-hidden="true" />
        <div>
          <h1 className="text-2xl font-semibold text-slate-900" data-testid="hti-title">
            Health Tourism at TreatX
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            A brief overview of how cross-border treatment works on our platform.
          </p>
        </div>
      </header>

      <section className="mt-8 space-y-6 text-sm text-slate-700 leading-relaxed">
        <div>
          <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <HeartHandshake className="h-4 w-4 text-primary" aria-hidden="true" />
            How it works
          </h2>
          <p className="mt-2">
            TreatX connects Patients with independent healthcare Providers across
            multiple countries. You can compare treatments, request quotes,
            communicate with clinics, book appointments, and pay through the
            Platform. When your treatment involves travel elements — for example
            airport transfers, translation, or accommodation — those are either
            supplied by the Provider as part of their package, or by a third-party
            partner disclosed at booking. TreatX does not sell flights, insurance,
            or immigration services.
          </p>
        </div>

        <div>
          <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" aria-hidden="true" />
            Provider verification at a glance
          </h2>
          <p className="mt-2">
            Before a Provider is listed on TreatX, we review company
            registration, healthcare licences and authorisations, and available
            Practitioner credentials. Where those checks are complete to our
            reasonable satisfaction, the Provider&apos;s profile may display a
            verification indicator. Verification is a platform-governance
            control, not a clinical guarantee. Providers remain independently
            responsible for the medical care they deliver.
          </p>
        </div>

        <div>
          <h2 className="text-base font-semibold text-slate-900">What to expect</h2>
          <ul className="mt-2 space-y-1.5 list-disc pl-5">
            <li>Transparent quotations with itemised inclusions and exclusions.</li>
            <li>Structured records of every quote, booking, payment, and refund.</li>
            <li>Secure communication and document sharing with your Provider.</li>
            <li>A Cancellation &amp; Refund Policy attached to each Order.</li>
            <li>A Health Tourism Booking Addendum when your booking includes travel elements.</li>
            <li>A support and complaint channel for any issue that arises.</li>
          </ul>
          <p className="mt-3 text-slate-600">
            We strongly recommend appropriate travel and medical-complication
            insurance for any cross-border treatment.
          </p>
        </div>
      </section>

      <hr className="my-8 border-slate-200" />

      <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 text-sm text-slate-700">
        <p>
          <strong>This page is informational only.</strong> The complete legal
          framework — including the Marketplace Model, Provider Relationship,
          Cross-Border Nature of the Service, Health Tourism Framework, refund
          principles, and dispute resolution — is set out in the{' '}
          <Link to="/terms" className="text-primary underline font-medium" data-testid="hti-link-terms">
            TreatX Patient Terms of Service
          </Link>
          , which is the sole authoritative source.
        </p>
      </div>

      <div className="mt-6 flex items-center gap-3">
        <Link
          to="/terms"
          className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
          data-testid="hti-cta-terms"
        >
          Read the Patient Terms of Service <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
        <span className="text-slate-300">·</span>
        <Link
          to="/contact"
          className="text-sm text-slate-600 hover:underline"
          data-testid="hti-cta-contact"
        >
          Contact TreatX
        </Link>
      </div>
    </div>
  </div>
);

export default HealthTourismInfoPage;
